<script setup>
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash-es'
import { ALERTS_URL, TOXPRINTS_URL } from '@/main'
import StdizerChemical from '@/components/stdizer/StdizerChemical'
import HazardPopover from '@/components/hazard/HazardPopover'
import ScoreRecordsModal from '@/components/hazard/ScoreRecordsModal'
import ChemCell from '@/components/chemical/ChemCell.vue'
import { compareChemicals } from '@/utils/chemical'

const props = defineProps({
  report: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  collapse: { type: Boolean, default: false },
  showDetails: { type: Boolean, default: false },
  showImages: { type: Boolean, default: false },
  showAlertsOnly: { type: Boolean, default: true },
  search: { type: String, default: null },
})

const emit = defineEmits(['update:selectedStructures', 'toggle-select-all'])

const showRecords = ref(false)
const hazardName = ref(null)
const scoreStructure = ref(null)
const basicHeaders = [
  { text: '#', value: 'ordinal', width: '2rem', class: 'align-top pt-1' },
  {
    text: 'CASRN, Name',
    value: 'cas',
    width: '200px',
    class: 'align-top pt-1 position-sticky sticky-header bg-light ',
    cellClass: 'position-sticky sticky-cell bg-light',
  },
]

const selectedRows = ref([])
const selectedStructures = computed(() => selectedRows.value.map((row) => row.chemical))

const onToggleSelectAll = ({ value }) => {
  const addAllFiltered = () => {
    const newRows = filteredRecords.value.filter(
      (row) => !selectedRows.value.some((selectedRow) => selectedRow.chemical.chemId === row.chemical.chemId),
    )

    return [...selectedRows.value, ...newRows]
  }

  const subtractSelected = () =>
    selectedRows.value.filter(
      (row) => !filteredRecords.value.some((item) => item.chemical.chemId === row.chemical.chemId),
    )

  selectedRows.value = value ? addAllFiltered() : subtractSelected()
}

const headers = computed(() =>
  basicHeaders.concat(
    props.report?.options?.alerts
      ? props.collapse
        ? [{ text: 'Alert', value: 'alert' }]
        : props.report.options.alertsInfo.map((ai) => ({
            text: ai.name,
            value: ai.name,
            description: ai.description,
            sort: customSort,
            class: 'align-top pt-1',
          }))
      : [],
  ),
)

const shownRecords = computed(() => {
  if (!props.report?.alerts) return []

  return props.showAlertsOnly
    ? props.report.alerts.filter((alert) => alert.alertFlags.some((flagGroup) => flagGroup.flags.length))
    : props.report.alerts
})

const filterRow = (row, search) => {
  if (!row) return false

  const query = search?.toLowerCase() || ''
  if (!query) return true

  const chemicalFields = row?.chemical ? Object.values(row.chemical) : []
  const alertFlagsValues = Array.isArray(row?.alertFlags) ? Object.values(row.alertFlags) : []

  return (
    chemicalFields.some((field) => typeof field === 'string' && field.toLowerCase().includes(query)) ||
    alertFlagsValues
      .flatMap((flagGroup) => flagGroup.flags)
      .some((flag) => typeof flag.text === 'string' && flag.text.toLowerCase().includes(query)) ||
    alertFlagsValues.some((flagGroup) => flagGroup.flags?.length && flagGroup.name?.toLowerCase().includes(query))
  )
}

const filteredRecords = computed(() => shownRecords.value.filter((row) => filterRow(row, props.search)))

watch(
  filteredRecords,
  (newValue) => {
    const filteredSelectedRows = newValue.filter((row) =>
      selectedRows.value.some((selectedRow) => selectedRow.chemical.chemId === row.chemical.chemId),
    )

    selectedRows.value = cloneDeep(filteredSelectedRows)
  },
  { immediate: true },
)

watch(
  selectedStructures,
  (newValue) => {
    emit('update:selectedStructures', newValue)
  },
  { immediate: true },
)

const customSort = (items, index, isDesc) => {
  let sorted = items

  if (!index.length) return items

  if (index[0] === 'ordinal') sorted = items.sort((left, right) => left.ordinal - right.ordinal)
  else if (index[0] === 'cas')
    sorted = items.sort((left, right) => compareChemicals(left?.chemical, right?.chemical, isDesc))
  else if (index[0] === 'alert')
    sorted = items.sort((left, right) => collapsedAlert(left).flags.length - collapsedAlert(right).flags.length)
  else sorted = items.sort((left, right) => itemFlagsLength(left, index[0]) - itemFlagsLength(right, index[0]))

  return isDesc[0] ? sorted.reverse() : sorted
}

const showScoreRecordsModal = (chemical, flag) => {
  if (flag?.endpoint) {
    scoreStructure.value = chemical
    hazardName.value = flag.endpoint.id
    showRecords.value = true
  }
}

const collapsedAlert = (item) => ({
  name: 'collapsed',
  flags: item.alertFlags.reduce((accumulator, flagGroup) => accumulator.concat(flagGroup.flags), []),
})

const itemFlags = (item, col) => item.alertFlags.find((flagGroup) => flagGroup.name === col)?.flags ?? []

const itemFlagsLength = (item, col) => itemFlags(item, col).length

const itemFlagByType = (item, col, type) => itemFlags(item, col).find((flag) => flag.type === type)
</script>

<template>
  <div class="d-flex flex-column h-100 w-100">
    <v-data-table
      id="alerts-table"
      v-if="shownRecords?.length"
      v-model="selectedRows"
      :headers="headers"
      :items="filteredRecords"
      :items-per-page="50"
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, 100, -1],
      }"
      :hide-default-footer="report?.alerts?.length <= 10"
      fixed-header
      :custom-sort="customSort"
      class="h-100 d-flex flex-column"
      show-select
      item-key="chemical.chemId"
      @toggle-select-all="onToggleSelectAll"
    >
      <template
        v-for="column in headers"
        #[`header.${column.value}`]="{ header: headerItem }"
      >
        <span
          :key="column.value"
          v-b-tooltip="{ customClass: 'header-tooltip' }"
          :title="headerItem.description"
        >
          {{ headerItem.text }}
        </span>
      </template>

      <template #[`item.cas`]="{ item }">
        <ChemCell
          :chemical="item.chemical"
          :request-chemical="item.requestChemical"
          :show-images="showImages"
        />
      </template>

      <template #[`item.alert`]="{ item }">
        <div :set="alert = collapsedAlert(item)">
          <div v-if="alert.flags.length > 0">
            <div v-if="!showDetails">
              <v-badge
                v-if="alert.flags.length > 1"
                :content="alert.flags.length"
                overlap
              >
                <v-icon
                  v-b-tooltip
                  :title="alert.flags.map((f) => f.text).join(', ')"
                  color="red"
                  large
                >
                  mdi-alert-rhombus-outline
                </v-icon>
              </v-badge>
              <v-icon
                v-else
                v-b-tooltip
                :title="alert.flags.map((f) => f.text).join(', ')"
                color="red"
                large
              >
                mdi-alert-rhombus-outline
              </v-icon>
            </div>
            <div v-else>
              <div
                v-for="(check, j) in alert.flags"
                :key="j"
              >
                <img
                  v-if="check.type === 'TOXPRINT'"
                  :src="`${TOXPRINTS_URL}/render?label=${encodeURIComponent(check.value)}`"
                  alt=""
                />
                <span v-else-if="check.type === 'HAZARD'" />
                <img
                  v-else
                  :src="`${ALERTS_URL}/render?smiles=${encodeURIComponent(check.value)}`"
                  alt=""
                  style="width: 250px; height: 150px"
                />
                <div>{{ check.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template
        v-for="(col, i) in report?.options?.alerts"
        #[`item.${col}`]="{ index, item }"
      >
        <div
          :key="i"
          :set="flags = itemFlags(item, col)"
        >
          <div v-if="flags.length">
            <div
              v-if="!showDetails"
              :set="hazardFlag = itemFlagByType(item, col, 'HAZARD')"
            >
              <v-badge
                v-if="flags.length > 1"
                :content="flags.length"
                overlap
              >
                <v-icon
                  :id="`${col}-${index}`"
                  v-b-tooltip
                  :title="flags.map((f) => f.text).join(', ')"
                  color="red"
                  large
                >
                  mdi-alert-rhombus-outline
                </v-icon>
              </v-badge>
              <v-icon
                v-else
                :id="`${col}-${index}`"
                :title="flags.map((f) => f.text).join(', ')"
                v-b-tooltip
                color="red"
                large
                @click="showScoreRecordsModal(item.chemical, itemFlagByType(item, col, 'HAZARD'))"
              >
                mdi-alert-rhombus-outline
              </v-icon>
              <HazardPopover
                v-if="hazardFlag"
                :delay="500"
                :hazard-category="hazardFlag.endpoint.id"
                :hazard-chemical="item.chemical.sid || item.chemical.casrn || item.chemical.smiles"
                :target="`${col}-${index}`"
                first-only
              />
            </div>
            <div v-else>
              <div
                v-for="(check, j) in flags"
                :key="`${i}-${j}`"
              >
                <img
                  v-if="check.type === 'TOXPRINT'"
                  :src="`${TOXPRINTS_URL}/render?label=${encodeURIComponent(check.value)}`"
                  alt=""
                />
                <span v-else-if="check.type === 'HAZARD'" />
                <img
                  v-else
                  :src="`${ALERTS_URL}/render?smiles=${encodeURIComponent(check.value)}`"
                  alt=""
                  style="width: 250px; height: 150px"
                />
                <div>{{ check.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #[`item.chemical`]="{ item }">
        <StdizerChemical
          :api-url="ALERTS_URL"
          :chemical="item.chemical"
          :show-details="showDetails"
          source="alerts"
        />
      </template>

      <template #[`item.comment`]="{ item }">
        <div style="white-space: pre">
          {{ item.comment }}
        </div>
      </template>
    </v-data-table>

    <template v-else>
      <div
        v-if="!loading"
        class="mt-15 text-center"
      >
        {{
          !!report?.alerts?.length && !filteredRecords?.length
            ? "No alerts found. Toggle 'Show Empty' in the display options to view empty rows."
            : 'No data to display'
        }}
      </div>
    </template>

    <ScoreRecordsModal
      v-model="showRecords"
      :chemical="scoreStructure"
      :hazard-name="hazardName"
    />
  </div>
</template>

<style lang="scss" scoped>
::v-deep #alerts-table .v-data-table__wrapper {
  display: flex;
  flex-direction: column;
  overflow: auto;
  flex-basis: 0 !important;
  flex-grow: 1 !important;
}

.header-tooltip .tooltip-inner {
  max-width: 500px !important;
}

//to make footer not so tall
::v-deep #alerts-table .v-data-footer__select .v-select {
  margin: 5px 0 5px 34px;
}
</style>
